/* -------------------------- Design imports start -------------------------- */
import { Box, CircularProgress, Grid, TextField, Typography } from '@mui/material'

/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom'

import logo from '../../assets/logo_black.svg'
import Button from '../../components/Button'
import Page from '../../components/layout/Page'
import useMobileService from '../../hooks/useMobileService'
import LogTool from '../../logger/logTools'
import { 
  useVerifyEmailToken, 
  useCheckPasswordResetToken, 
  useConfirmPasswordReset 
} from '../../api/hooks/useAuthMutations'
/* ------------------------- Functional imports end ------------------------- */

enum VerifyEvent {
  invite = 'invite',
  signup = 'signup',
  resetPassword = 'reset_password',
  changeEmail = 'changeEmail',
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function VerifyPage() {
  /* -------------------------- Non state data start -------------------------- */
  const location: any = useLocation()
  const history = useHistory()
  // get token from url after ?token=
  const token = new URLSearchParams(location.search).get('token')
  const passwordRegex =
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ])[A-Za-z0-9^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]{8,256}$/
  const { t } = useTranslation(['common'])
  const log = new LogTool({ context: 'VerifyPage', enable: true, logLevel: 'warn' })
  
  // React Query Hooks
  const verifyEmailTokenMutation = useVerifyEmailToken()
  const checkPasswordResetTokenMutation = useCheckPasswordResetToken()
  const confirmPasswordResetMutation = useConfirmPasswordReset()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [invalidPassword, setInvalidPassword] = useState(false)
  const [notSamePassword, setNotSamePassword] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const isMobile = useMobileService()
  const [password, setPassword] = useState<string | undefined>(undefined)
  const [repeatPassword, setRepeatPassword] = useState<string | undefined>(undefined)
  const [verifyEvent, setVerifyEvent] = useState<VerifyEvent | undefined>(undefined)
  const [verifyToken, setVerifyToken] = useState<string | undefined>(undefined)
  const { event } = useParams<{ event: VerifyEvent }>()
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    if (!event || !token) return

    setVerifyToken(token)
    setVerifyEvent(event)

    void (async () => {
      const isValid = await validateTokenOnLoad()
      if (!isValid) return

      if (event === VerifyEvent.changeEmail || event === VerifyEvent.signup) {
        await verifyEmailToken(undefined)
      }
      if (event === VerifyEvent.invite) {
        // For invite events we only need the token to be valid up front; the user will set a password.
        return
      }
      if (event === VerifyEvent.resetPassword) {
        // Token already validated above; just render the password form.
        return
      }
    })()
  }, [event, token])

  // Clear password mismatch error when passwords match
  useEffect(() => {
    if (notSamePassword && password && repeatPassword && password === repeatPassword) {
      setNotSamePassword(false)
    }
  }, [password, repeatPassword, notSamePassword])

  // Clear invalid password error when password becomes valid
  useEffect(() => {
    if (invalidPassword && password && passwordRegex.test(password)) {
      setInvalidPassword(false)
    }
  }, [password, invalidPassword])
  /* ------------------------------- Effects end ------------------------------ */

  log.debug('verifyEvent', verifyEvent)
  log.debug('verifyToken', verifyToken)

  /* ------------------------- Utility functions start ------------------------ */
  async function verifyEmailToken(password: string | undefined, checkTokenOnly = false) {
    if (!token) {
      setError(true)
      return
    }
    
    setLoading(true)
    
    try {
      await verifyEmailTokenMutation.mutateAsync({
        token,
        ...(password && { password }),
      })
      
      if (!checkTokenOnly) {
        history.push('/signIn', password ? { inviteSuccess: true } : { orgSignUpSuccess: true })
      } else {
        setLoading(false)
      }
    } catch (err: any) {
      log.error('Email token verification failed', err)
      
      // If checking token only (for invite), only show error if token is invalid
      // If password is required, that means token is valid
      if (checkTokenOnly) {
        const errorData = err?.response?.data || {}
        const passwordError = errorData.password?.[0] || errorData.non_field_errors?.[0] || ''
        const tokenError = errorData.token?.[0] || ''
        const statusCode = err?.response?.status || 0
        
        log.debug('Token check error data:', { errorData, passwordError, tokenError, statusCode, fullError: err })
        
        // If error is specifically about password being required, token is valid - don't show error
        // This is the only case where we don't show an error for invite token check
        if (passwordError && passwordError.toLowerCase().includes('password is required')) {
          // Token is valid, password is just required - this is expected for invite
          log.debug('Token is valid, password required - not showing error')
          setLoading(false)
          return
        }
        
        // Any other error (token invalid, network error, server error, etc.) - show error
        log.debug('Token check failed with error, showing error state')
        setError(true)
        setLoading(false)
      } else {
        setError(true)
        setLoading(false)
      }
    }
  }

  async function verifyPasswordToken(password: string | undefined) {
    if (!token) {
      setError(true)
      return
    }
    
    setLoading(true)
    
    try {
      if (password) {
        // Confirm password reset with new password
        await confirmPasswordResetMutation.mutateAsync({
          token,
          password,
        })
        history.push('/signIn', { passwordResetSuccess: true })
      } else {
        // Just check if token is valid
        await checkPasswordResetTokenMutation.mutateAsync(token)
        setLoading(false)
      }
    } catch (err) {
      log.error('Password token verification failed', err)
      setError(true)
      setLoading(false)
    }
  }

  async function validateTokenOnLoad(): Promise<boolean> {
    if (!token) {
      setError(true)
      return false
    }

    setLoading(true)
    try {
      await checkPasswordResetTokenMutation.mutateAsync(token)
      setLoading(false)
      return true
    } catch (err) {
      log.error('Token validation failed', err)
      setError(true)
      setLoading(false)
      return false
    }
  }

  /*async function resendEmailLink() {
    setLoading(true)
    log.debug(event)
    setLoading(false)
  }*/
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const passwordIsValid = (password: string) => {
    return password && passwordRegex.test(password)
  }

  const passwordsAreEqual = () => {
    return password === repeatPassword
  }

  async function handleSubmit(e: any, type = 'email'): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    e.preventDefault()
    if (password && passwordIsValid(password)) {
      // For reset_password events, there's no repeatPassword field, so skip password equality check
      if (type === 'password' || passwordsAreEqual()) {
        type === 'email' ? await verifyEmailToken(password) : await verifyPasswordToken(password)
      } else {
        setNotSamePassword(true)
      }
    } else {
      setInvalidPassword(true)
    }
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const VerifyEmailOrgAdmin = <></>

  const VerifyEmailUser = (
    <>
      <Grid item xs={12}>
        <Typography variant="h5" align="center">
          {t('content.auth.signUp')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" align="center">
          {t('content.auth.choosePasswordText')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="password"
          type="password"
          label={t('content.auth.password')}
          variant="outlined"
          fullWidth
          helperText={t('common:content.loginForm.passwordHelperText')}
          error={invalidPassword}
          onChange={e => setPassword(e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="repeatPassword"
          type="password"
          label={t('content.loginForm.repeatPassword')}
          variant="outlined"
          fullWidth
          helperText={
            notSamePassword
              ? t('common:content.loginForm.repeatPasswordHelperText')
              : t('common:content.loginForm.repeatPasswordPlaceholder')
          }
          error={notSamePassword}
          onChange={e => setRepeatPassword(e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <Button variant="contained" fullWidth onClick={handleSubmit}>
          {t('content.auth.signUp')}
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="info"
          fullWidth
          onClick={() => history.push('/signIn')}
          style={{ textTransform: 'none' }}
        >
          {t('content.auth.backToSignIn')}
        </Button>
      </Grid>
    </>
  )

  const VerifyNewEmailUser = <div>Verify New User Email</div>

  const VerifyNewPasswordUser = (
    <>
      <Grid item xs={12}>
        <Typography variant="h5" align="center">
          {t('content.auth.changePassword')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" align="center">
          {t('content.auth.choosePasswordText')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="password"
          type="password"
          label={t('content.auth.password')}
          variant="outlined"
          fullWidth
          helperText={t('common:content.loginForm.passwordHelperText')}
          error={invalidPassword}
          onChange={e => setPassword(e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="contained"
          fullWidth
          onClick={(e) => void (async () => await handleSubmit(e, 'password'))()}
          style={{ color: 'whitesmoke', textTransform: 'none' }}
        >
          {t('content.auth.changePassword')}
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="info"
          fullWidth
          onClick={() => history.push('/signIn')}
          style={{ textTransform: 'none' }}
        >
          {t('content.auth.backToSignIn')}
        </Button>
      </Grid>
    </>
  )

  const VerificationError = (
    <>
      <Grid item xs={12}>
        <Typography variant="h5" align="center">
          {t('content.auth.verificationError', 'Verification error')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" align="center">
          {t('content.auth.verificationErrorText', 'The verification link is invalid or has expired.')}
        </Typography>
      </Grid>
      {/* <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => resendEmailLink()}
        >
          {t('auth.resendEmailLink')}
        </Button>
      </Grid> */}
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="info"
          fullWidth
          onClick={() => history.push('/signIn')}
          style={{ textTransform: 'none' }}
        >
          {t('content.auth.backToSignIn')}
        </Button>
      </Grid>
    </>
  )

  // return 404 page if event is not valid
  if (!Object.values(VerifyEvent).includes(event)) {
    return <Redirect to="/404" />
  }
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <Page>
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Grid
          container
          spacing={2}
          width={isMobile ? '100%' : '60%'}
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12} md={12} display="flex" justifyContent="center">
            <img src={logo} alt="logo" style={{ width: '200px', height: 'auto' }} />
          </Grid>
          {loading && (
            <Grid item xs={12} md={12} display="flex" justifyContent="center">
              <CircularProgress />
            </Grid>
          )}
          {event === VerifyEvent.signup && !error && VerifyEmailOrgAdmin}
          {event === VerifyEvent.invite && !error && VerifyEmailUser}
          {event === VerifyEvent.changeEmail && !error && VerifyNewEmailUser}
          {event === VerifyEvent.resetPassword && !error && VerifyNewPasswordUser}
          {error && VerificationError}
        </Grid>
      </Box>
    </Page>
  )
}
